import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

import useAnimate from '../ui/Motion';

import WordReveal from '../ui/WordReveal';
import Reveal from '../ui/Reveal';
import { site } from '../data/site';

/**
 * The subpage counterpart to the home hero: the same two-plane parallax
 * and the same crossing-the-top-edge departure, at about a third of the
 * amplitude and two thirds of the height. A subpage should not spend a
 * whole screen introducing itself.
 */
export default function PageHero({
    eyebrow,
    title,
    lead,
    backdrop,
    focus = '50% 50%',
    crumbs = [],
    actions,
    children
}) {
    const ref = useRef(null);
    const reduced = !useAnimate();
    const live = !reduced;

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start start', 'end start']
    });

    const contentY = useTransform(scrollYProgress, [0, 1], ['0vh', '-6vh']);
    const contentOpacity = useTransform(scrollYProgress, [0.25, 0.85], [1, 0]);
    const backdropY = useTransform(scrollYProgress, [0, 1], ['0vh', '6vh']);
    const backdropScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.12]);

    /* Only the crumbs that name a real page become list items with a URL;
       a bare label is still a position in the trail, just not a link. */
    const trail = [{ label: 'Home', href: '/' }, ...crumbs];

    return (
        <section className={`page-hero${backdrop ? '' : ' page-hero--plain'}`} ref={ref}>
            {crumbs.length > 0 && (
                <Helmet>
                    <script type="application/ld+json">
                        {JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'BreadcrumbList',
                            itemListElement: trail.map((crumb, i) => ({
                                '@type': 'ListItem',
                                position: i + 1,
                                name: crumb.label,
                                ...(crumb.href ? { item: `${site.origin}${crumb.href}` } : {})
                            }))
                        })}
                    </script>
                </Helmet>
            )}

            {backdrop && (
                <motion.div
                    className="page-hero__backdrop"
                    aria-hidden="true"
                    style={live ? { y: backdropY, scale: backdropScale } : undefined}
                >
                    <img src={backdrop} alt="" style={{ '--focus': focus }} />
                </motion.div>
            )}

            <div className="page-hero__aurora" aria-hidden="true"><span /></div>
            <div className="page-hero__vignette" aria-hidden="true" />

            <motion.div
                className="page-hero__content"
                style={live ? { y: contentY, opacity: contentOpacity } : undefined}
            >
                {crumbs.length > 0 && (
                    <Reveal y={12} amount={0.1}>
                        <nav aria-label="Breadcrumb">
                            <ol className="crumbs">
                                <li><Link to="/">Home</Link></li>
                                {crumbs.map((crumb, i) => (
                                    <li key={crumb.label}>
                                        {crumb.href && i < crumbs.length - 1 ? (
                                            <Link to={crumb.href}>{crumb.label}</Link>
                                        ) : (
                                            <span aria-current="page">{crumb.label}</span>
                                        )}
                                    </li>
                                ))}
                            </ol>
                        </nav>
                    </Reveal>
                )}

                {eyebrow && (
                    <Reveal y={14} delay={0.05} amount={0.1}>
                        <span className="t-eyebrow t-eyebrow--accent" style={{ marginTop: '1.1rem' }}>
                            {eyebrow}
                        </span>
                    </Reveal>
                )}

                <WordReveal
                    as="h1"
                    className="t-h1 page-hero__title"
                    text={title}
                    delay={0.12}
                />

                {lead && (
                    <Reveal delay={0.22} amount={0.1}>
                        <p className="t-lead page-hero__lead">{lead}</p>
                    </Reveal>
                )}

                {actions && (
                    <Reveal delay={0.3} amount={0.1} className="page-hero__actions">
                        {actions}
                    </Reveal>
                )}

                {children}
            </motion.div>
        </section>
    );
}
